import { RestChatService } from '@repo/network'
import { KeycloakVO } from '@repo/types'
import { SnackbarProvider } from '@tecsinapse/cortex-react'
import { enableAnalytics } from '@web/analytics'
import {
  ErrorBoundary,
  GlobalLoading,
  KC_INFO,
  KeycloakProvider,
  KeycloakUserInfo,
  read,
  remove,
  store,
  UPDATE_USER
} from '@web/modules/core'
import { Publicity } from '@web/modules/publicity'
import { PreLogin } from '@web/modules/pre-login'
import { ServerMaintenance } from '@web/modules/server-maintenance'
import { WingoSetup } from '@web/modules/wingo-setup/ui'
import axios from 'axios'
import Keycloak from 'keycloak-js'
import React from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'

axios.defaults.baseURL = import.meta.env.VITE_BACKEND_URL
const params = new URLSearchParams(window.location.search)
const realmParam = params.get('kc_idp_hint')

const initRoot = () => {
  const container = document.getElementById('root') as HTMLElement
  return createRoot(container)
}

const configAxiosInterceptors = (kcInstance: Keycloak, kcInfo: KeycloakVO) => {
  axios.interceptors.request.use(async config => {
    if (kcInstance.isTokenExpired(0)) {
      try {
        await kcInstance.updateToken(1)
      } catch {
        await kcInstance.login()
      }
    }

    config.headers.set('Authorization', `Bearer ${kcInstance.token}`)
    config.headers.set('Realm', kcInstance.realm)
    config.headers.set('ClientUUID', kcInfo.clientUUID)

    return config
  })
}

const createChatInitConfig = () => {
  const connectionKey = params.get('connectionKey')
  const clientCode = params.get('clientCode')
  const phoneNumber = params.get('phoneNumber')

  return {
    userGuiding: import.meta.env.VITE_APP_PROFILE === 'production',
    updateUserDestinations: import.meta.env.VITE_APP_PROFILE !== 'development',
    connectionKey: connectionKey ?? undefined,
    clientCode: clientCode ?? undefined,
    phoneNumber: phoneNumber ?? undefined
  }
}

const createKeycloakInstance = (kcInfo: { realm: string; clientId: string }) => {
  return new Keycloak({
    url: import.meta.env.VITE_KEYCLOAK_HOST,
    realm: kcInfo.realm,
    clientId: kcInfo.clientId
  })
}

async function renderPath() {
  if (window.location.pathname.includes('/setup')) {
    await renderWingoSetup()
    return
  }

  await checkLogin()
}

// login process starts here
renderPath().then()

async function checkLogin() {
  try {
    let kcInfo = read(KC_INFO)

    if (!kcInfo) {
      store(UPDATE_USER, true)
    }

    if (realmParam && !kcInfo) {
      kcInfo = await RestChatService.keycloakProps(realmParam)
    }

    const kcInstance = createKeycloakInstance(kcInfo)

    const isAuthenticatedUser = await kcInstance.init({
      onLoad: 'check-sso',
      checkLoginIframe: false
    })

    if (!isAuthenticatedUser) {
      remove(KC_INFO)
      throw Error('User not authenticated, redirected to pre-login screen')
    }

    store(KC_INFO, kcInfo)

    await kcInstance.loadUserInfo()
    await renderChat(kcInstance, kcInfo)
  } catch (e) {
    const root = initRoot()

    root.render(
      <SnackbarProvider options={{ position: 'bottom-center' }}>
        <PreLogin realmParam={realmParam} />
      </SnackbarProvider>
    )
  }
}

async function renderChat(kcInstance: Keycloak, kcInfo: KeycloakVO) {
  const root = initRoot()
  const chatInitConfig = createChatInitConfig()
  configAxiosInterceptors(kcInstance, kcInfo)

  const currentUser = kcInstance.userInfo as KeycloakUserInfo

  root.render(
    <SnackbarProvider options={{ position: 'bottom-center' }}>
      <GlobalLoading />
    </SnackbarProvider>
  )

  try {
    let hasConnections = true
    try {
      if (read(UPDATE_USER)) {
        await RestChatService.updateUser({
          userId: currentUser.sub,
          userName: currentUser.name,
          destinations: []
        }).catch(() => {
          console.error('User cannot be updated')
        })
        store(UPDATE_USER, false)
      }

      const { connectionKeys } = await RestChatService.info()
      hasConnections = (connectionKeys ?? []).length > 0
    } catch (e) {
      console.error(e)
      hasConnections = true
    }

    if (!hasConnections) {
      root.render(
        <ErrorBoundary>
          <KeycloakProvider
            info={kcInfo}
            instance={kcInstance}
          >
            <SnackbarProvider options={{ position: 'bottom-center' }}>
              <Publicity />
            </SnackbarProvider>
          </KeycloakProvider>
        </ErrorBoundary>
      )
      await enableAnalytics(kcInstance)
      return
    }

    root.render(
      <App
        kcInfo={kcInfo}
        kcInstance={kcInstance}
        chatInitConfig={chatInitConfig}
        currentUser={currentUser}
      />
    )
  } catch (e: any) {
    root.render(<ServerMaintenance />)
  }
}

async function renderWingoSetup() {
  const root = initRoot()

  try {
    root.render(
      <SnackbarProvider>
        <WingoSetup />
      </SnackbarProvider>
    )
  } catch (e: any) {
    root.render(<ServerMaintenance />)
  }
}
